/**
 * 日志查询优化工具
 * 提供防抖、缓存和请求去重功能
 */

import { LogQueryParams, LogRecord, LogStatistics, LogResult, LogLevel } from '../types/log';
import { logService } from '../services/logService';

// 查询缓存接口
interface QueryCache {
  data: LogRecord[];
  total: number;
  timestamp: number;
}

// 统计缓存接口
interface StatisticsCache {
  data: LogStatistics;
  timestamp: number;
}

// 全局缓存
const queryCache = new Map<string, QueryCache>();
const statisticsCache = new Map<string, StatisticsCache>();

// 正在进行的请求
const pendingRequests = new Map<string, Promise<any>>();

// 缓存过期时间（30秒）
const CACHE_EXPIRE_TIME = 30 * 1000;

// 防抖延迟（500ms）
const DEBOUNCE_DELAY = 500;

class LogQueryOptimizer {
  private debounceTimers = new Map<string, NodeJS.Timeout>();

  /**
   * 生成缓存键
   */
  private generateCacheKey(params: LogQueryParams): string {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        const value = params[key as keyof LogQueryParams];
        if (value !== undefined && value !== '') {
          result[key] = value;
        }
        return result;
      }, {} as any);
    
    return JSON.stringify(sortedParams);
  }

  /**
   * 检查缓存是否过期
   */
  private isExpired(timestamp: number): boolean {
    return Date.now() - timestamp > CACHE_EXPIRE_TIME;
  }

  /**
   * 防抖查询日志列表
   */
  async queryLogs(params: LogQueryParams, immediate = false): Promise<{ data: LogRecord[]; total: number }> {
    const cacheKey = this.generateCacheKey(params);
    
    // 检查缓存
    const cached = queryCache.get(cacheKey);
    if (cached && !this.isExpired(cached.timestamp)) {
      return { data: cached.data, total: cached.total };
    }

    // 检查是否有正在进行的请求
    const pendingRequest = pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    const queryFunction = async () => {
      try {
        const response = await logService.getList(params);
        if (response.code === 200) {
          const result = {
            data: response.data.records || [],
            total: response.data.total || 0,
          };
          
          // 更新缓存
          queryCache.set(cacheKey, {
            data: result.data,
            total: result.total,
            timestamp: Date.now(),
          });
          
          return result;
        } else {
          throw new Error(response.message || '查询失败');
        }
      } finally {
        // 清除请求状态
        pendingRequests.delete(cacheKey);
      }
    };

    if (immediate) {
      // 立即执行
      const requestPromise = queryFunction();
      pendingRequests.set(cacheKey, requestPromise);
      return requestPromise;
    } else {
      // 防抖执行
      return new Promise((resolve, reject) => {
        // 清除之前的定时器
        const existingTimer = this.debounceTimers.get(cacheKey);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        // 设置新的定时器
        const timer = setTimeout(async () => {
          try {
            const requestPromise = queryFunction();
            pendingRequests.set(cacheKey, requestPromise);
            const result = await requestPromise;
            resolve(result);
          } catch (error) {
            reject(error);
          }
          this.debounceTimers.delete(cacheKey);
        }, DEBOUNCE_DELAY);

        this.debounceTimers.set(cacheKey, timer);
      });
    }
  }

  /**
   * 查询统计信息
   */
  async queryStatistics(params: Partial<LogQueryParams>): Promise<LogStatistics> {
    const cacheKey = `statistics_${this.generateCacheKey(params)}`;
    
    // 检查缓存
    const cached = statisticsCache.get(cacheKey);
    if (cached && !this.isExpired(cached.timestamp)) {
      return cached.data;
    }

    // 检查是否有正在进行的请求
    const pendingRequest = pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    const queryFunction = async () => {
      try {
        const response = await logService.getStatistics(params);
        if (response.code === 200) {
          const result = response.data;
          
          // 更新缓存
          statisticsCache.set(cacheKey, {
            data: result,
            timestamp: Date.now(),
          });
          
          return result;
        } else {
          throw new Error(response.message || '查询统计失败');
        }
      } finally {
        // 清除请求状态
        pendingRequests.delete(cacheKey);
      }
    };

    const requestPromise = queryFunction();
    pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /**
   * 清除缓存
   */
  clearCache(queryParams?: LogQueryParams): void {
    if (queryParams) {
      const cacheKey = this.generateCacheKey(queryParams);
      queryCache.delete(cacheKey);
      statisticsCache.delete(`statistics_${cacheKey}`);
    } else {
      queryCache.clear();
      statisticsCache.clear();
    }
  }

  /**
   * 预加载常用查询
   */
  async preloadCommonQueries(): Promise<void> {
    const commonQueries: LogQueryParams[] = [
      { current: 1, size: 10 },
      { current: 1, size: 10, result: LogResult.SUCCESS },
      { current: 1, size: 10, result: LogResult.FAILED },
      { current: 1, size: 10, level: LogLevel.ERROR },
    ];

    const promises = commonQueries.map(query => this.queryLogs(query, true));
    await Promise.all(promises);
  }

  /**
   * 获取缓存状态
   */
  getCacheStatus(): { queryCache: number; statisticsCache: number; pendingRequests: number } {
    return {
      queryCache: queryCache.size,
      statisticsCache: statisticsCache.size,
      pendingRequests: pendingRequests.size,
    };
  }
}

// 创建全局实例
export const logQueryOptimizer = new LogQueryOptimizer();

export default logQueryOptimizer;
